/* T115 — Audit AI project list (Sprint 5 / FR-028).
 *
 * Mirrors `New UI Changes/audit-ai.html`. Lists all Projects with a
 * mini-pipeline indicator per row (DD ✓ · 2 SPA · 3 Risk) plus the
 * Project's overall status badge.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatListingDate } from '../utils/formatListingDate';
import { getProject, listProjects } from '../services/api';
import type {
  ProjectDetail,
  ProjectListItem,
  ProjectOverallStatus,
  StageRefStatus,
} from '../types/api';

/* Sprint 5 client feedback 2026-06-22 — phantom-ID fix.
   The ProjectListItem returned by /api/v1/projects only includes
   pipeline status; the DD/SPA/Risk child assessment IDs (which
   the report URLs need) are NOT in the list payload. The old
   approach computed report URLs via
     `${audit_id.replace('AUDIT-', 'SPA-')}`
   which only worked when project audit_id and assessment_id
   shared the same sequence number — they DO NOT in general.
   AUDIT-2026-0030 may have SPA-2026-0014 as its SPA child.

   Enrich each row with the real child IDs via per-row
   getProject() so the action buttons can link to the actual
   report — same pattern RecentProjectsTable already uses. */
interface ProjectRowWithIds extends ProjectListItem {
  dd_id?: string | null;
  spa_id?: string | null;
}

/* Sprint 5 client feedback 2026-06-25 (Issues/FE doc #3) — Pipeline
   column re-designed to match the Dashboard's Recent Projects
   StageDot icons. Old MiniStep emitted numbered circles + DD/SPA/Risk
   labels at larger size; new MiniStep emits compact 20px icon-only
   circles per state (completed = green check, active = outlined,
   pending = grey lock-style ring). label/num props kept for API
   compatibility but no longer rendered (label survives as
   accessibility title + aria-label). */
function MiniStep({
  done,
  active,
  label,
  num: _num,
}: {
  done: boolean;
  active: boolean;
  label: string;
  num: number;
}): JSX.Element {
  void _num;
  const state = done ? 'complete' : active ? 'in_progress' : 'pending';
  const colour =
    state === 'complete' ? { bg: 'var(--pass)', fg: '#fff' }
    : state === 'in_progress' ? { bg: 'var(--info)', fg: '#fff' }
    : { bg: 'var(--surface-alt)', fg: 'var(--muted)' };
  const icon =
    state === 'complete' ? <polyline points="20 6 9 17 4 12" />
    : state === 'in_progress' ? (
        <>
          <circle cx="12" cy="12" r="2" />
          <circle cx="5" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </>
      )
    : (
        <>
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </>
      );
  return (
    <span
      aria-label={`${label}: ${state.replace('_', ' ')}`}
      data-tip={`${label}: ${state.replace('_', ' ')}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: colour.bg,
        color: colour.fg,
        border: state === 'pending' ? '1px solid var(--lines)' : 'none',
      }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {icon}
      </svg>
    </span>
  );
}

function StatusLabel(status: StageRefStatus): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

const PAGE_SIZE = 20;

export function AuditAIListPage(): JSX.Element {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectRowWithIds[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<ProjectOverallStatus | ''>('');
  // Sprint 5 client feedback 2026-06-22 — Audit AI list pass 1:
  // analyst filter (per design audit doc item #2). Server doesn't
  // index by analyst yet, so this is a client-side filter applied
  // AFTER the page fetch.
  const [analystFilter, setAnalystFilter] = useState<string>('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void reload();

    async function reload(): Promise<void> {
      setLoading(true);
      try {
        const resp = await listProjects({
          status: statusFilter || undefined,
          q: query || undefined,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        });
        // Sprint 5 client feedback 2026-06-22 — enrich each row with
        // its real DD/SPA assessment IDs so the action buttons link
        // to the correct report. Without this we fell back to the
        // phantom-ID assumption (audit_id sequence == assessment_id
        // sequence) which fails whenever a project's child
        // assessments were created out-of-band. Per-row getProject()
        // is bounded by PAGE_SIZE (20) so the parallel fetch is
        // safe. Failed lookups gracefully fall back to the bare
        // ProjectListItem.
        const enriched = await Promise.all(
          resp.projects.map(async (p): Promise<ProjectRowWithIds> => {
            try {
              const detail: ProjectDetail = await getProject(p.audit_id);
              return {
                ...p,
                dd_id: detail.dd_assessment?.id ?? null,
                spa_id: detail.spa_assessment?.id ?? null,
              };
            } catch {
              return p;
            }
          }),
        );
        setProjects(enriched);
        setTotal(resp.total);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    }
  }, [statusFilter, query, page]);

  /* Reset to first page when filters change. */
  useEffect(() => {
    setPage(0);
  }, [statusFilter, query]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstShown = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastShown = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className="page-body">
      <div className="page-topbar">
        <div>
          <div className="breadcrumb">
            <a href="/dashboard">Dashboard</a>
            <span className="bc-sep">/</span>
            <span>Audit AI</span>
          </div>
          <h1 className="page-topbar-title">Audit AI</h1>
          <p className="page-topbar-sub">
            Each project runs a 3-stage audit pipeline.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/projects/new')}>
          + New Project
        </button>
      </div>

      <div className="filter-bar" role="search">
        <div className="filter-search-wrap" style={{ position: 'relative' }}>
          <svg
            className="search-icon"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--muted)',
              pointerEvents: 'none',
            }}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="form-input"
            type="search"
            placeholder="Search projects…"
            aria-label="Search projects"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ paddingLeft: 30 }}
          />
        </div>
        <div className="filter-selects">
          <select
            className="form-select"
            aria-label="Status"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as ProjectOverallStatus | '')
            }
          >
            <option value="">All Statuses</option>
            <option value="in_progress">In Progress</option>
            <option value="flagged">Flagged</option>
            <option value="complete">Completed</option>
            <option value="failed">Failed</option>
            {/* Sprint 5 client feedback 2026-07-08 (FE Ticket —
                Cancelled) — Cancelled is filterable alongside
                Failed. Filter = Failed still excludes cancelled
                rows via the enum-exact match on overall_status. */}
            <option value="cancelled">Cancelled</option>
          </select>
          {/* Sprint 5 client feedback 2026-06-22 — Audit AI list pass 1:
              analyst dropdown (design audit doc item #2). Options
              auto-derived from the analyst_name values present on the
              current page's projects. Disabled when no analyst data
              is yet loaded. */}
          <select
            className="form-select"
            aria-label="Analyst"
            value={analystFilter}
            onChange={(e) => setAnalystFilter(e.target.value)}
            disabled={!projects || projects.length === 0}
          >
            <option value="">All Analysts</option>
            {Array.from(
              new Set(
                (projects || [])
                  .map((p) => p.analyst_name)
                  .filter((n): n is string => Boolean(n)),
              ),
            )
              .sort()
              .map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
          </select>
        </div>
        <div className="filter-actions" style={{ display: 'flex', gap: 'var(--s2)' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setQuery('');
              setStatusFilter('');
              setAnalystFilter('');
            }}
          >
            Clear
          </button>
          {/* Sprint 5 UI-SP3-3 — Apply button. Filters already
              auto-apply on change, but the design has an explicit
              Apply CTA for analysts used to that pattern.  */}
          <button
            type="button"
            className="btn btn-accent btn-sm"
            onClick={() => setPage(0)}
            data-tip="Apply filters"
          >
            Apply
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-fail" role="alert">
          {error}
        </div>
      )}

      {/* Sprint 5 client feedback 2026-06-22 (Second-Set FE Issue 2):
          re-added "Showing N projects" — the design doc explicitly
          asks for it above the table, between the filter bar and
          the data rows. */}
      <p className="results-label">
        {loading ? 'Loading…' : (
          <>Showing <strong>{(projects || []).length}</strong> {(projects || []).length === 1 ? 'project' : 'projects'}{total && total > (projects || []).length ? <> of <strong>{total}</strong></> : null}</>
        )}
      </p>

      {/* Sprint 5 client feedback 2026-06-22 — analyst filter applied
          client-side over the server page. We accept that this means
          paging across analyst-filtered results may surface fewer
          than PAGE_SIZE rows on a given page. */}
      {(() => {
        const visibleProjects = (projects || []).filter((p) => {
          if (analystFilter && p.analyst_name !== analystFilter) return false;
          return true;
        });
        const hasRows = visibleProjects.length > 0;
        return (<>
      {hasRows && (
        <div className="table-wrap audit-ai-table-card">
          {/* Sprint 5 client feedback 2026-06-22 (Second-Set FE Issue 2):
              compact table styling — dropped --spacious modifier in
              favour of the dense enterprise default; the audit-ai-
              table-card wrapper adds rounded card corners + bordered
              container per the design doc. */}
          <table className="data-table data-table--compact" aria-label="Audit AI projects">
            <thead>
              <tr>
                <th>Project</th>
                <th>Analyst</th>
                <th>Created</th>
                <th>Pipeline</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleProjects.map((p) => {
                const dd = p.pipeline.dd.status;
                const spa = p.pipeline.spa.status;
                const risk = p.pipeline.risk.status;
                const ddDone = dd === 'complete';
                const spaDone = spa === 'complete';
                const riskDone = risk === 'complete' || risk === 'degraded';
                const overallLabel =
                  p.overall_status === 'in_progress' ? 'In Progress'
                  : p.overall_status === 'complete' ? 'Completed'
                  : p.overall_status === 'flagged' ? 'Flagged'
                  : p.overall_status === 'failed' ? 'Failed'
                  /* Sprint 5 client feedback 2026-07-08 (FE Ticket —
                     Cancelled) — explicit branch BEFORE the
                     StatusLabel fallback so cancelled deals render
                     "Cancelled" with neutral styling, not the info
                     blue default. */
                  : p.overall_status === 'cancelled' ? 'Cancelled'
                  : StatusLabel(p.overall_status as StageRefStatus);
                // Sprint 5 client feedback 2026-06-22 — Audit AI list pass 1:
                // canonical .badge classes instead of inline color-mix.
                // Same palette + sizing as the rest of the app
                // (Dashboard, SPA Section 04, etc.), automatically
                // honours the dark-mode --info / --pass / --watch /
                // --fail overrides added in ead2640.
                const statusBadgeClass =
                  p.overall_status === 'complete' ? 'badge-pass'
                  : p.overall_status === 'flagged' ? 'badge-watch'
                  : p.overall_status === 'failed' ? 'badge-fail'
                  /* Sprint 5 client feedback 2026-07-08 (FE Ticket —
                     Cancelled) — neutral pill, matching StatusChip's
                     canonical cancelled → badge-neutral mapping. */
                  : p.overall_status === 'cancelled' ? 'badge-neutral'
                  : 'badge-info';
                return (
                  <tr
                    key={p.audit_id}
                    onClick={() => navigate(`/projects/${p.audit_id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span className="td-name" style={{ fontWeight: 600 }}>
                        {p.project_name}
                      </span>
                      <br />
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '0.68rem',
                          color: 'var(--muted)',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {p.audit_id}
                      </span>
                    </td>
                    {/* Sprint 5 UI-SP3-3 — Analyst + Created columns.
                        Backend now resolves lead_analyst_id → analyst_name
                        via a User JOIN so we render the actual person.
                        Falls back to truncated UUID only when the analyst
                        row is missing (referential-integrity edge case). */}
                    <td className="t-sm">
                      <span style={{ color: 'var(--text-sec)', fontSize: '0.82rem' }}>
                        {p.analyst_name
                          ? p.analyst_name
                          : p.lead_analyst_id
                          ? String(p.lead_analyst_id).slice(0, 8) + '…'
                          : '—'}
                      </span>
                    </td>
                    <td className="t-muted" style={{ fontSize: '0.82rem' }}>
                      {/* Sprint 5 client feedback 2026-07-07 (UI Issues #5) —
                          route listing dates through the shared formatter so
                          every listing table shows "DD MMM YYYY" regardless
                          of the browser locale. */}
                      {formatListingDate(p.created_at ?? null)}
                    </td>
                    <td>
                      <div style={{ display: 'inline-flex', gap: 'var(--s2)', alignItems: 'center' }}>
                        <MiniStep done={ddDone} active={!ddDone} label="DD" num={1} />
                        <span className="s5-pipeline-line" style={{ width: 16 }} />
                        <MiniStep done={spaDone} active={ddDone && !spaDone} label="SPA" num={2} />
                        <span className="s5-pipeline-line" style={{ width: 16 }} />
                        <MiniStep done={riskDone} active={spaDone && !riskDone} label="Risk" num={3} />
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${statusBadgeClass}`}>
                        {overallLabel}
                      </span>
                    </td>
                    <td
                      style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        style={{
                          display: 'inline-flex',
                          gap: 6,
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <ActionEyeButton onClick={() => navigate(`/projects/${p.audit_id}`)} />
                        <ListPill
                          label="DD"
                          color="var(--dd-accent)"
                          enabled={ddDone && Boolean(p.dd_id)}
                          onClick={() => {
                            if (p.dd_id) navigate(`/reports/${p.dd_id}`);
                          }}
                        />
                        <ListPill
                          label="SPA"
                          color="var(--spa-accent)"
                          enabled={spaDone && Boolean(p.spa_id)}
                          onClick={() => {
                            if (p.spa_id) navigate(`/reports/${p.spa_id}`);
                          }}
                        />
                        <ListPill
                          label="DR"
                          tipLabel="Draft Report"
                          color="var(--risk-accent)"
                          enabled={riskDone}
                          onClick={() => navigate(`/projects/${p.audit_id}/risk-report`)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Sprint 5 client feedback 2026-06-22 — Audit AI list pass 1:
              hide the pagination footer when there's only one page of
              results. Per the design audit doc item #10, the expected
              design has no pagination visible on the standard view.
              When the result set spans multiple pages we still surface
              it so analysts can navigate. */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--s3) var(--s4)',
                borderTop: '1px solid var(--lines)',
              }}
            >
              <span className="t-xs t-muted">
                Showing {firstShown}–{lastShown} of {total} projects
              </span>
              <div style={{ display: 'flex', gap: 'var(--s2)', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ← Prev
                </button>
                <span className="t-xs t-muted">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {projects && projects.length === 0 && !loading && (
        <div className="empty-state" style={{ textAlign: 'center', padding: 'var(--s8)' }}>
          <p>No projects yet.</p>
          <button className="btn btn-primary" onClick={() => navigate('/projects/new')}>
            Create your first project
          </button>
        </div>
      )}

      {/* Empty-state for filter-no-match (server returned rows, but
          analystFilter or another client-side filter cleared them). */}
      {projects && projects.length > 0 && !hasRows && (
        <div className="empty-state" style={{ textAlign: 'center', padding: 'var(--s6)' }}>
          <p className="t-muted">No projects match the current filters.</p>
        </div>
      )}
        </>);
      })()}
    </div>
  );
}

function ActionEyeButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      data-tip="Open project"
      aria-label="Open project"
      onClick={onClick}
      style={{
        marginRight: 8,
        background: 'transparent',
        border: '1px solid var(--lines)',
        borderRadius: '6px',
        padding: '4px 6px',
        cursor: 'pointer',
        color: 'var(--text-sec)',
        verticalAlign: 'middle',
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    </button>
  );
}

function ListPill({
  label,
  tipLabel,
  color,
  enabled,
  onClick,
}: {
  /** Visible pill text — DD / SPA / DR. */
  label: string;
  /** Name of the artefact the pill opens. Defaults to `<label> report`.
   *  The DR pill opens the "Draft Report", so it can't be derived. */
  tipLabel?: string;
  color: string;
  enabled: boolean;
  onClick: () => void;
}): JSX.Element {
  const what = tipLabel ?? `${label} report`;
  /* data-tip lives on a wrapper, not the button: a disabled control emits no
     mouse events, so the delegated tooltip would never fire — and the
     disabled state is exactly when "not available yet" needs explaining. */
  return (
    <span
      data-tip={enabled ? `Open ${what}` : `${what} not available yet`}
      style={{ display: 'inline-flex', verticalAlign: 'middle' }}
    >
    <button
      type="button"
      onClick={() => enabled && onClick()}
      disabled={!enabled}
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        background: enabled
          ? `color-mix(in srgb, ${color} 18%, transparent)`
          : 'var(--surface-alt)',
        color: enabled ? color : 'var(--muted)',
        border: `1px solid ${enabled ? color : 'var(--lines)'}`,
        borderRadius: '12px',
        fontFamily: 'var(--font-body, inherit)',
        fontSize: '0.7rem',
        fontWeight: 700,
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.6,
        letterSpacing: '0.04em',
        verticalAlign: 'middle',
        minWidth: 38,
        textAlign: 'center',
      }}
    >
      {label}
    </button>
    </span>
  );
}
